import { ANALYTICS_ACTION_CLICKED, ButtonClickedEvent } from '@atlassian/mpac-analytics';
import { postUserInstallConsent } from '@atlassian/mpac-api-client';
import { AppInstallPermission, isCompatibleMarketplaceProduct } from '@atlassian/mpac-services-client';
import { useOneClickInstallSiteSelector } from '@atlassian/mpac-site-selection';
import { makeCapabilitySetToEdition } from '@atlassian/mpac-spi';
import { AppDeployment } from '@atlassian/mpac-types';
import { MarketplaceAppPaymentModel, MarketplaceStoreEditionType, MarketplaceStoreInstallationTargetProduct, MarketplaceStoreInstalledAppManageLinkType } from '@atlassian/mpac-types/graphql-gateway-types';
import { flowUtils, WindowUtils } from '@atlassian/mpac-utils';
import { useMemo } from 'react';
import { ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ACTIVATE_TRIAL_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ADD_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ADD_TO_SANDBOX_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_CONFIGURE_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_GET_STARTED_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_INSTALL_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_MANAGE_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_REQUEST_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_REVIEW_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_START_FREE_TRIAL_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_TRY_ADVANCED_APP_BUTTON, ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_TRY_STANDARD_APP_BUTTON } from './events';
const {
  Free,
  Standard
} = MarketplaceStoreEditionType;
export const FREE_PRIMARY_CTA_LABEL = 'Get it now';
export const PAID_PRIMARY_CTA_LABEL = 'Start free trial';
export const ADD_TO_SANDBOX_CTA_LABEL = 'Add to sandbox';
export const CLOUD_MIGRATION_TRIAL_CTA_LABEL = 'Activate trial';
export const REQUEST_APP_CTA_LABEL = 'Request app';
export const REVIEW_APP_CTA_LABEL = 'Review';
export const GET_STARTED_CTA_LABEL = 'Get started';
export const TRY_ADVANCED_CTA_LABEL = 'Try Advanced';
export const TRY_STANDARD_CTA_LABEL = 'Try Standard';
export const ADD_APP_CTA_LABEL = 'Add';
export const FREE_PLAN_CTA_LABEL = 'Install app';
export const ctaLabelToAnalyticsSubjectIdMap = {
  [FREE_PRIMARY_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_GET_STARTED_BUTTON,
  [PAID_PRIMARY_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_START_FREE_TRIAL_BUTTON,
  [ADD_TO_SANDBOX_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ADD_TO_SANDBOX_BUTTON,
  [CLOUD_MIGRATION_TRIAL_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ACTIVATE_TRIAL_BUTTON,
  [REQUEST_APP_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_REQUEST_APP_BUTTON,
  [REVIEW_APP_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_REVIEW_APP_BUTTON,
  [GET_STARTED_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_GET_STARTED_BUTTON,
  [TRY_ADVANCED_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_TRY_ADVANCED_APP_BUTTON,
  [TRY_STANDARD_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_TRY_STANDARD_APP_BUTTON,
  [ADD_APP_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_ADD_APP_BUTTON,
  [MarketplaceStoreInstalledAppManageLinkType.Manage]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_MANAGE_APP_BUTTON,
  [MarketplaceStoreInstalledAppManageLinkType.Configure]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_CONFIGURE_APP_BUTTON,
  [MarketplaceStoreInstalledAppManageLinkType.GetStarted]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_GET_STARTED_BUTTON,
  [FREE_PLAN_CTA_LABEL]: ANALYTICS_SUBJECT_ID_TWO_STEP_INSTALL_DIALOG_INSTALL_APP_BUTTON
};
const InstalledAppButtonLabel = Object.freeze({
  [MarketplaceStoreInstalledAppManageLinkType.GetStarted]: 'Get started',
  [MarketplaceStoreInstalledAppManageLinkType.Configure]: 'Configure',
  [MarketplaceStoreInstalledAppManageLinkType.Manage]: 'Manage'
});
export const resolveInstallationCTALabel = _ref => {
  let {
    isFreeApp,
    isCloudMigrationTrialEligible,
    isEligibleForFreeOnSandbox,
    isEditionsSupported,
    eligibleForFreeOnSandboxWithEditions,
    isEditionChargedSeparatelyInSandbox,
    isEligibleForMIL,
    isMILandAppEditionsEnabled,
    selectedPlan
  } = _ref;
  if (isEditionsSupported && eligibleForFreeOnSandboxWithEditions) {
    return ADD_TO_SANDBOX_CTA_LABEL;
  }
  if (isEditionChargedSeparatelyInSandbox) {
    return PAID_PRIMARY_CTA_LABEL;
  }
  if (isEligibleForFreeOnSandbox) {
    return ADD_TO_SANDBOX_CTA_LABEL;
  }
  if (isEditionsSupported && selectedPlan === MarketplaceStoreEditionType.Free) {
    return FREE_PLAN_CTA_LABEL;
  }
  if (isMILandAppEditionsEnabled) {
    return ADD_APP_CTA_LABEL;
  }
  if (isEligibleForMIL) {
    return ADD_APP_CTA_LABEL;
  }
  if (isFreeApp) {
    return FREE_PRIMARY_CTA_LABEL;
  }
  if (isCloudMigrationTrialEligible) {
    return CLOUD_MIGRATION_TRIAL_CTA_LABEL;
  }
  return PAID_PRIMARY_CTA_LABEL;
};
export const getManageAppUrl = (appKey, url) => {
  return "".concat(url, "/plugins/servlet/upm#manage/").concat(appKey);
};
export const getNextButtonText = (isAppInstalled, canInstallApp, installedAppManageLink, isAdvancedEditionAvailable, editionInstalledOnSelectedSite, isEditionsEnabled) => {
  // For end users, always show "Request app" CTA label
  if (!canInstallApp) {
    return REQUEST_APP_CTA_LABEL;
  }

  // Admin - Edition already installed
  if (isEditionsEnabled && isAppInstalled) {
    if (editionInstalledOnSelectedSite === Standard && isAdvancedEditionAvailable) {
      return TRY_ADVANCED_CTA_LABEL;
    } else if (editionInstalledOnSelectedSite === Free) {
      return TRY_STANDARD_CTA_LABEL;
    }
  }

  // Admin - Manage app
  if (isAppInstalled && installedAppManageLink !== null && installedAppManageLink !== void 0 && installedAppManageLink.type) {
    return InstalledAppButtonLabel[installedAppManageLink.type];
  }

  // Admin - App not installed
  return REVIEW_APP_CTA_LABEL;
};
export const isNextButtonDisabled = (isAppInstalled, installedAppManageLink, canInstallApp, isAdvancedEditionsWithHAMSBilling, isAdvancedEditionAvailable, selectedSiteAndProduct, editionInstalledOnSelectedSite) => {
  // Disable button if no site is selected
  if (!selectedSiteAndProduct) {
    return true;
  }
  // Disable button if 'Advanced' edition is selected for a site with HAMS billing
  if (isAdvancedEditionsWithHAMSBilling) {
    return true;
  }

  //  Do not disable the button for end users
  if (!canInstallApp) {
    return false;
  }

  // Do not disable if app is installed, installed edition is "Standard" and "Advanced" edition is supported
  if (isAppInstalled && editionInstalledOnSelectedSite === Standard) {
    if (isAdvancedEditionAvailable) {
      return false;
    }
    return true;
  }

  // Do not disable if app is installed and installed edition is "Free"
  if (isAppInstalled && editionInstalledOnSelectedSite === Free) {
    return false;
  }

  // Disable if app is installed and manage link is not available.
  return selectedSiteAndProduct && isAppInstalled && !(installedAppManageLink !== null && installedAppManageLink !== void 0 && installedAppManageLink.url);
};
export const navigateToManageApp = (appKey, siteUrl) => {
  window.location.href = getManageAppUrl(appKey, siteUrl);
};
const navigateToBillingConsole = (links, orgId) => {
  const billingConsoleUrl = "".concat(links.adminHub.expand({}), "/o/").concat(orgId, "/billing-preview");
  WindowUtils.openPage(billingConsoleUrl, WindowUtils.BLANK_WINDOW);
};

// eslint-disable-next-line max-lines-per-function
export const handleNextButtonClick = async _ref2 => {
  let {
    selectedSiteAndProduct,
    isAppInstalled,
    currentStep,
    nextButtonText,
    resolveInstallationCTA,
    onSiteSelectorFooterInstallCTAClick,
    requestAppHrefResolver,
    installAppHrefResolver,
    installedAppHrefResolver,
    steps,
    links,
    installedAppManageLink,
    selectedPlan,
    orgId,
    appDetails,
    fireAnalyticsEvent,
    enableUserConsentRecording = false,
    userCount
  } = _ref2;
  if (!selectedSiteAndProduct) {
    return false;
  }
  if (nextButtonText === REQUEST_APP_CTA_LABEL) {
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      event: ANALYTICS_ACTION_CLICKED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ctaLabelToAnalyticsSubjectIdMap[REQUEST_APP_CTA_LABEL],
      attributes: {
        appKey: appDetails.appKey,
        appId: appDetails.appId
      }
    });
    onSiteSelectorFooterInstallCTAClick(true, undefined, requestAppHrefResolver(selectedSiteAndProduct, selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.value));
    return false;
  }
  if ([TRY_ADVANCED_CTA_LABEL, TRY_STANDARD_CTA_LABEL].includes(nextButtonText)) {
    if (orgId) {
      fireAnalyticsEvent({
        eventType: ButtonClickedEvent,
        event: ANALYTICS_ACTION_CLICKED,
        eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
        actionSubjectId: ctaLabelToAnalyticsSubjectIdMap[nextButtonText],
        attributes: {
          appKey: appDetails.appKey,
          appId: appDetails.appId
        }
      });
      navigateToBillingConsole(links, orgId);
    }
    return false;
  }
  if (isAppInstalled) {
    if (installedAppManageLink !== null && installedAppManageLink !== void 0 && installedAppManageLink.url) {
      fireAnalyticsEvent({
        eventType: ButtonClickedEvent,
        event: ANALYTICS_ACTION_CLICKED,
        eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
        actionSubjectId: ctaLabelToAnalyticsSubjectIdMap[MarketplaceStoreInstalledAppManageLinkType.Manage],
        attributes: {
          appKey: appDetails.appKey,
          appId: appDetails.appId
        }
      });
      WindowUtils.redirectTo(installedAppHrefResolver(installedAppManageLink === null || installedAppManageLink === void 0 ? void 0 : installedAppManageLink.url));
    }
    return false;
  }
  if (currentStep && (resolveInstallationCTA === FREE_PRIMARY_CTA_LABEL || resolveInstallationCTA === PAID_PRIMARY_CTA_LABEL || resolveInstallationCTA === ADD_APP_CTA_LABEL || resolveInstallationCTA === ADD_TO_SANDBOX_CTA_LABEL)) {
    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      event: ANALYTICS_ACTION_CLICKED,
      eventContainer: ANALYTICS_EVENT_CONTAINER_TWO_STEP_INSTALL_DIALOG,
      actionSubjectId: ctaLabelToAnalyticsSubjectIdMap[resolveInstallationCTA],
      attributes: {
        appKey: appDetails.appKey,
        appId: appDetails.appId
      }
    });

    // Record user consent for this app installation (only if feature is enabled)
    if (enableUserConsentRecording) {
      await postUserInstallConsent({
        appKey: appDetails.appKey
      }).catch(error => {
        console.error('Failed to record user install consent:', error);
        // Continue with installation even if consent recording fails
      });
    }
    onSiteSelectorFooterInstallCTAClick(true, undefined, installAppHrefResolver(selectedSiteAndProduct, true, selectedPlan === null || selectedPlan === void 0 ? void 0 : selectedPlan.value, userCount));
    return false;
  }
  return currentStep < steps.length - 1;
};
export const getProductBaseUrl = selectedSiteAndProduct => {
  if (!selectedSiteAndProduct) {
    return '';
  }
  const {
    site,
    product
  } = selectedSiteAndProduct;
  switch (product) {
    case 'jira':
      return site.url;
    case 'confluence':
      return "".concat(site.url, "/wiki");
    case 'compass':
      return site.url;
    default:
      throw flowUtils.unexpectedCase(product);
  }
};
export const getInstalledAppManageLink = (appDetails, installedAppDetails, selectedSiteAndProduct) => {
  const isForgeApp = appDetails.deploymentType === AppDeployment.ECOSYSTEM_CLOUD;
  const installedAppManageLink = installedAppDetails === null || installedAppDetails === void 0 ? void 0 : installedAppDetails.installedAppManageLink;
  if (!installedAppManageLink || !installedAppManageLink.url || !installedAppManageLink.type) {
    return null;
  }

  /* Construct the compelete URL for a Forge app */
  if (isForgeApp) {
    return {
      url: "".concat(getProductBaseUrl(selectedSiteAndProduct)).concat(installedAppManageLink.url),
      type: installedAppManageLink.type
    };
  }
  return {
    url: installedAppManageLink.url,
    type: installedAppManageLink.type
  };
};
export const useGetSiteSelectorData = (appDetails, compatibleProducts, stargateBasePath, showAppPlans) => {
  var _installedAppDetailsP3, _installedAppDetailsP4;
  const appCompatibilities = useMemo(() => compatibleProducts.filter(product => isCompatibleMarketplaceProduct(product)), [compatibleProducts]);
  const {
    availableSitesPromise,
    appInstallPermissionPromise,
    selectedSiteAndProduct,
    installedAppDetailsPromise,
    onSiteChange,
    hostStatusPromise,
    billingSystemPromise
  } = useOneClickInstallSiteSelector(appDetails.appKey, appCompatibilities, stargateBasePath, showAppPlans, undefined, false);
  const isFreeApp = appDetails.paymentModel === MarketplaceAppPaymentModel.Free;
  const {
    installedAppDetailsResult
  } = installedAppDetailsPromise;
  const installedAppDetails = installedAppDetailsResult === null || installedAppDetailsResult === void 0 ? void 0 : installedAppDetailsResult.marketplaceStore.installedAppDetails;
  const isAppInstalled = isFreeApp ? installedAppDetails === null || installedAppDetails === void 0 ? void 0 : installedAppDetails.installed : (installedAppDetails === null || installedAppDetails === void 0 ? void 0 : installedAppDetails.installed) && (installedAppDetails === null || installedAppDetails === void 0 ? void 0 : installedAppDetails.licenseActive);
  const installedAppManageLink = isAppInstalled ? getInstalledAppManageLink(appDetails, installedAppDetails, selectedSiteAndProduct) : null;
  const canUserInstallApp = useMemo(() => {
    const permission = appInstallPermissionPromise === null || appInstallPermissionPromise === void 0 ? void 0 : appInstallPermissionPromise.appInstallPermissionResult;
    return permission === AppInstallPermission.CAN_INSTALL_FREE_AND_PAID || isFreeApp && permission === AppInstallPermission.CAN_INSTALL_FREE_ONLY;
  }, [appInstallPermissionPromise, isFreeApp]);
  const editionInstalledOnSelectedSite = useMemo(() => {
    var _installedAppDetailsP, _installedAppDetailsP2;
    const editionInstalledValue = installedAppDetailsPromise === null || installedAppDetailsPromise === void 0 ? void 0 : (_installedAppDetailsP = installedAppDetailsPromise.installedAppDetailsResult) === null || _installedAppDetailsP === void 0 ? void 0 : (_installedAppDetailsP2 = _installedAppDetailsP.marketplaceStore) === null || _installedAppDetailsP2 === void 0 ? void 0 : _installedAppDetailsP2.installedAppDetails.edition;
    if (!editionInstalledValue) {
      return null;
    }
    return makeCapabilitySetToEdition(editionInstalledValue);
  }, [installedAppDetailsPromise === null || installedAppDetailsPromise === void 0 ? void 0 : (_installedAppDetailsP3 = installedAppDetailsPromise.installedAppDetailsResult) === null || _installedAppDetailsP3 === void 0 ? void 0 : (_installedAppDetailsP4 = _installedAppDetailsP3.marketplaceStore) === null || _installedAppDetailsP4 === void 0 ? void 0 : _installedAppDetailsP4.installedAppDetails.edition]);
  return useMemo(() => ({
    isFreeApp,
    isAppInstalled,
    canUserInstallApp,
    selectedSiteAndProduct,
    onSiteChange,
    hostStatusPromise,
    installedAppDetailsPromise,
    availableSitesPromise,
    appInstallPermissionPromise,
    editionInstalledOnSelectedSite,
    installedAppManageLink,
    billingSystemPromise
  }), [isFreeApp, isAppInstalled, canUserInstallApp, selectedSiteAndProduct, onSiteChange, hostStatusPromise, installedAppDetailsPromise, availableSitesPromise, appInstallPermissionPromise, editionInstalledOnSelectedSite, installedAppManageLink, billingSystemPromise]);
};
export const ProductNameToTargetProductMap = {
  confluence: MarketplaceStoreInstallationTargetProduct.Confluence,
  jira: MarketplaceStoreInstallationTargetProduct.Jira,
  compass: MarketplaceStoreInstallationTargetProduct.Compass
};